import { callPostApi } from "api/api";
import { AppContext } from "Context/AppContext";
import { OrderConfigContext } from "Context/OrderConfigContext";
import { useFormikContext } from "formik";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import { useOrderAttributeProvider } from "../context/order";
import {
  CUSTOM_ANYTIME,
  pstToUTCDateTime,
  timeStamptoPSTime,
  utcDateTimeToTime,
  CUSTOM_DEFAULT_TIME
} from "../utility/util";
import { useDate } from "./useDates";

export function useDelivery() {
  const { module, orderDetail } = useOrderAttributeProvider();
  
  const [enableTimeEdit, setEnableTimeEdit] = useState(
    module === "edit-order" ? false : true
  );
  const { showToastMessage } = useContext(AppContext);
  const formik = useFormikContext();
  let subscription_order = formik.values.is_subscribe_order === 1;
  const config = useContext(OrderConfigContext);
  const [matrix, setMatrix] = useState([]);
  const [timeslectionDropdown, setTimeslectionDropdown] = useState(false);
  const { blockoutDates, tooltipDays, upchargeDatesArray } = useDate();
  let show = useMemo(() => {
    return {
      two_field: formik.values.delivery_information.delivery_type_id > 2,
      all_field: formik.values.delivery_information.delivery_type_id === 4,
    };
  }, [formik.values.delivery_information.delivery_type_id]);

  /**
   * Processes delivery timing response and updates the delivery matrix and formik values.
   * 
   * This function handles the transformation of delivery timing API responses into a format
   * suitable for display in dropdowns. It processes delivery options, filters them based on
   * address type, and updates both the matrix state and formik form values.
   * 
   * @param {Object} timingResponse - The API response containing delivery timing data
   * @param {Object} timingResponse.data.data - The nested data object containing delivery information
   * @param {boolean} [timingResponse.data.data.changeDeliveryDateToNextDay] - Flag indicating if delivery date should be changed to next day
   * @param {Array} timingResponse.data.data.costList - Array of delivery cost/time options
   * @param {number} day - The selected delivery date (moment object or date string)
   * @param {Object} dat - Delivery information object to merge into formik values
   * @param {Object} upcharge - Upcharge information object containing upcharge_id and upcharge_amount
   * @param {boolean} [isBruq=false] - Flag indicating if this is a Burq delivery option
   * @param {boolean} [isConcatOptions=false] - Flag indicating if options should be concatenated to existing matrix instead of replacing
   * 
   * @returns {void}
   * 
   * @description
   * The function performs the following operations:
   * 1. Checks if delivery date needs to be changed to next day and shows error if unavailable
   * 2. Maps costList items to create delivery option objects with formatted times and charges
   * 3. Filters out "Anytime" options for address types > 2
   * 4. Updates matrix state (either replaces or concatenates based on flags)
   * 5. Enables time editing if disabled
   * 6. Updates formik values with delivery information, preserving existing values when concatenating
   * 
   * @example
   * // Standard delivery matrix update
   * CommonDeliveryFunc(timingResponse, selectedDate, deliveryData, upchargeData, false, false);
   * 
   * // Burq delivery with concatenation (preserves existing selections)
   * CommonDeliveryFunc(burqResponse, selectedDate, deliveryData, upchargeData, true, true);
   */
  async function CommonDeliveryFunc(timingResponse,day,dat,upcharge,isBruq =false, isConcatOptions = false){
    if (timingResponse?.data?.data?.changeDeliveryDateToNextDay ) {
      showToastMessage(
        undefined,
        `Delivery is currently unavailable for the selected date`,
        "error",
        false
      );
      setMatrix([])
      return;
    }
    
    // Create dropdown value
    let matrix = timingResponse.data.data.costList.map((mat) => {
      let charge = mat.price;
      let actual_delivered_time = mat.delivery_time;
      let delivery_time_type = mat.delivery_time_type;
      let id = mat.id ?? 0;
      let pst_time = timeStamptoPSTime(mat.delivery_time, formik?.values?.orderTimezone);
      let delivery_date_time = pstToUTCDateTime(day, mat.delivery_time, null, formik?.values?.orderTimezone);
      let utc_time = utcDateTimeToTime(delivery_date_time);
      let name = `${delivery_time_type} ${pst_time} for $${charge}`;
      let delivery_time = `${delivery_time_type} ${utc_time}`;
      if (delivery_time_type === "Anytime") {
        name = `${delivery_time_type} for $${charge}`;
      }
      let burq_quote_id = mat?.burq_quote_id ?? ""
      if (id === 0 && mat?.burq_quote_id && mat?.burq_quote_id != "") {
        name = `${delivery_time_type} for $${charge}`;
        id = mat?.delivery_time
      }
      return {
        charge,
        delivery_time_type,
        id,
        delivery_date_time,
        delivery_time,
        name,
        actual_delivered_time,
        burq_quote_id
      };
    });
    // (formik.values.delivery_information.address_type_id === 1 || formik.values.delivery_information.address_type_id === 2) &&  delivery_time_type === "Anytime"
    if (formik.values.delivery_information.address_type_id > 2) {
      matrix = matrix?.filter(
        (c) => (c.delivery_time_type !== "Anytime")
      );
    }

    if(isBruq && isConcatOptions){
      setMatrix((prev)=>[...prev,...matrix])
    }else{
      setMatrix(matrix);
    }
    if (!enableTimeEdit) {
      setEnableTimeEdit(true);
    }

    formik.setValues((v) => {
      return {
        ...v,
        delivery_information: {
          ...dat,
          ...upcharge,
          delivery_time_id: isBruq && isConcatOptions ? v?.delivery_information?.delivery_time_id:null ?? null,
          delivery_date_time: isBruq && isConcatOptions ? v?.delivery_information?.delivery_date_time: null ?? "",
          delivery_time: isBruq && isConcatOptions ? v?.delivery_information?.delivery_time:null ?? "",
          delivery_time_type: isBruq && isConcatOptions ? v?.delivery_information?.delivery_time_type:null ?? "",
          charge: isBruq && isConcatOptions ? v?.delivery_information?.charge:null ?? 0,
          actual_delivered_time:isBruq && isConcatOptions ? v?.delivery_information?.actual_delivered_time:null ?? "",
        },
      };
    });

  }
  const handleDateChange = async (day) => {
    //Pickup Order
    let dat = setDate(day);
    if (formik.values.delivery_information.order_type.toString() === "1") {
      setEnableTimeEdit(true);
      dat.upcharge_id = 0;
      dat.upcharge_amount = 0;
      formik.setValues((v) => {
        return {
          ...v,
          delivery_information: {
            ...dat,
            delivery_time_id: null,
            // delivery_date_time: "",
            // delivery_time: "",
            delivery_time_type: "",
            burq_quote_id:"",
            burq_quote_expires:""
          },
        };
      });
      return;
    }

    //Check if zip code and address type avaialable
    if (
      !formik.values.delivery_information.address_type_id ||
      formik.values.delivery_information.zipcode.length === 0
    ) {
      showToastMessage(
        undefined,
        `Please fill out address type and zip code before selecting delivery date.`,
        "error",
        false
      );
      return;
    }

    //Check Product is added before delivery matrix is selected.
    let length = 0;
    formik.values.recipients.forEach((element) => {
      length = length + element.products.length;
    });

    //comment for no validation apply for csr of lead time & holiday cutoff
    // if (length > 0 && formik.values.custom_order === 0) {
    //   //Check Holiday Cutoff
    //   if (!subscription_order) {
    //     if (holidayApplicable(day)) {
    //       return;
    //     }
    //   }

    //   //Check Leadtime
    //   let lap = await isLeadTimeApplicable(day);
    //   if (lap) {
    //     showToastMessage(
    //       undefined,
    //       `Delivery is not unavailable for the selected date due to unavailability of product.`,
    //       "error",
    //       false
    //     );
    //   }
    // }

    //Check if upcharge
    let upcharge = {};
    let up = upchargeDatesArray.find(
      (c) =>
        moment(c.date).format("MM-DD-YYYY") === moment(day).format("MM-DD-YYYY")
    );
    if (up) {
      upcharge = {
        upcharge_id: up?.id,
        upcharge_amount: up?.charge,
      };
    } else {
      upcharge = {
        upcharge_id: 0,
        upcharge_amount: 0,
      };
    }

    formik.setValues((v) => {
      return {
        ...v,
        delivery_information: {
          ...dat,
        },
      };
    });


    //get all option for Burq data fatching with delivery matrix
    // Check if it's not subscription order, order type is delivery, delivery address type is delivery service
    // delivery date time no blank, address line1, city, state,zip availabe 
    // and it's only for create order
    // and order is for single recipient
    
    //get total product price
    // Calculate total price of products
    let totalProductPrice = 0;
    await formik?.values?.recipients.map(async (recipientsObj) => {
      await recipientsObj?.products.map((productObj) => {
        totalProductPrice += productObj.total_price
      })
    })
    let allCurrentData = formik.values;
     let addressData = ""
    if(allCurrentData?.delivery_information?.delivery_type_id == 2 && 
      allCurrentData?.delivery_information?.address_line_1 != "" && 
      allCurrentData?.delivery_information?.zipcode != "" && 
      allCurrentData?.delivery_information?.state != "" && 
      allCurrentData?.delivery_information?.city != "" && 
      allCurrentData?.is_subscribe_order == 0 &&
      allCurrentData?.delivery_information?.order_type == 0 && 
      allCurrentData?.recipients?.length < 2 &&
      (module === 'edit-order' || (module !== 'edit-order' && totalProductPrice <= 400))
      ){
        //Burq implementation. only single recipient, only for delivery order
        addressData = (allCurrentData?.delivery_information?.address_line_1).trim()+", "
        addressData = addressData+(allCurrentData?.delivery_information?.city).trim()+", "
        addressData = addressData+(allCurrentData?.delivery_information?.state).trim()+", "
        addressData = addressData+(allCurrentData?.delivery_information?.zipcode).trim()+", "
      }
      setTimeslectionDropdown(true)
    //Get Delivery Matrix
    try {
      let timingResponse;
      if(module !== 'edit-order' || (module === 'edit-order' && orderDetail && !orderDetail?.is_burq_order)){
        timingResponse = await callPostApi({
          url: "delivery-matrix/list-by-date",
          body: {
            zip_code: formik.values.delivery_information.zipcode,
            address_type_id: formik.values.delivery_information.address_type_id,
            delivery_date: moment(day).format("MM/DD/YYYY"),
            order_by_time: moment().unix(),
            // notCheckRolloverFalg: true,
            full_address:addressData,
            address_2: formik.values.delivery_information.address_line_2 ?? "",
            attn: formik.values.delivery_information.attn ?? "",
            orderTimezone: formik?.values?.orderTimezone
          },
        });
        if(timingResponse?.data?.data){
          await CommonDeliveryFunc(timingResponse,day,dat,upcharge,false,false)
          setTimeslectionDropdown(false);
        }
      }

      let timingBurqResponse;
      if(!timingResponse?.data?.data.nonLocal){
        if(module !== 'edit-order' || (module === 'edit-order' && orderDetail && orderDetail?.is_burq_order)){
            timingBurqResponse = await callPostApi({
            url: "delivery-matrix/list-by-date-burq",
            body: {
              zip_code: formik.values.delivery_information.zipcode,
              address_type_id: formik.values.delivery_information.address_type_id,
              delivery_date: moment(day).format("MM/DD/YYYY"),
              order_by_time: moment().unix(),
              notCheckRolloverFalg: true,
              full_address:addressData,
              address_2: formik.values.delivery_information.address_line_2 ?? "",
              attn: formik.values.delivery_information.attn ?? "",
              orderTimezone: formik?.values?.orderTimezone
            },
          });
          if(timingBurqResponse?.data?.data || (module === 'edit-order' && orderDetail && orderDetail?.is_burq_order)){
            await CommonDeliveryFunc(timingBurqResponse,day,dat,upcharge,true, module === 'edit-order' ? false : true)
            setTimeslectionDropdown(false);
          }
        }
      }

      if(module !== 'edit-order' || (module === 'edit-order' && orderDetail && !orderDetail?.is_burq_order)){
        let tempObj = {
          actual_delivered_time: '',
          charge: 0,
          delivery_date_time: "",
          delivery_time: "",
          delivery_time_type: "",
          id: -1,
          name: "Custom",
        }
        setMatrix((prev)=>[...prev,tempObj])
      }
        // matrix.push(tempObj);
      
      if(timingResponse?.data?.data?.costList?.length <= 0 && timingBurqResponse?.data?.data?.costList?.length <= 0){
        showToastMessage(
          undefined,
          `Delivery is currently unavailable for the selected date`,
          "error",
          false
        );
        return;
      }
    } catch (error) {
      showToastMessage(
        undefined,
        `Facing technical issue with delivery-matrix`,
        "error",
        false
      );
    }

    //UTC LIST
  };

  //check holiday cutoff
  const holidayApplicable = (day) => {
    let sec = moment(day).unix();
    let appliedHoliday = config?.holidayCutOff?.filter(
      (c) => c.start_date <= sec && c.end_date > sec
    );

    if (appliedHoliday.length === 0) {
      return false;
    }

    let appliedCategories = config?.holidayCutOff?.map((c) => {
      return {
        category: c.holiday_cutoff_exclude_categories.map(
          (z) => z.category_hierarchy_id
        ),
      };
    });

    let products = checkCutOff(appliedCategories);
    if (products.length !== 0) {
      showToastMessage(
        undefined,
        `${products.join()} is not available due to holiday period.`,
        "error",
        false
      );
      return true;
    }

    return false;
  };

  //check cutoff
  function checkCutOff(apl) {
    let productTitle = [];
    formik.values?.recipients?.forEach((recipient) => {
      recipient?.products?.forEach((product) => {
        let productCatHeirs = product?.product_categories?.map(
          (c) => c.category_hierarchy_id
        );
        apl.forEach((a) => {
          a.category.forEach((id) => {
            if (!productCatHeirs.includes(id)) {
              productTitle.push(product.title);
            }
          });
        });
      });
    });
    return productTitle;
  }

  //check if lead time is applicable
  async function isLeadTimeApplicable(day) {
    let prodIds = new Set();
    formik.values.recipients.forEach((recipent) => {
      recipent.products.forEach((product) => {
        prodIds.add(product.id);
      });
    });

    let ids = Array.from(prodIds);

    let productBlockResponse = await callPostApi({
      url: "blockout-dates/list",
      body: {
        product_id: ids,
      },
    });

    if (productBlockResponse?.data?.data?.rows?.length === 0) {
      return false;
    } else {
      let curr = moment(day).unix();
      if (
        productBlockResponse.data.data.rows[0].start_blocking_on <= curr &&
        productBlockResponse.data.data.rows[0].stop_repeating_on > curr
      ) {
        return true;
      }
    }
    return false;
  }

  const setDate = (day) => {
    let delivery_information = {
      ...formik.values.delivery_information,
    };
    delivery_information.delivery_date = moment(day).format("YYYY-MM-DD");

    //SET DEFAULT TIME 3 PM
    let pstCustomTime = moment(moment(day).format('YYYY-MM-DD') + ' ' + CUSTOM_DEFAULT_TIME).format("hh:mm A");
    let delivery_date_time_custom = pstToUTCDateTime(
      delivery_information.delivery_date,
      null,
      pstCustomTime,
      formik?.values?.orderTimezone
    );
    let delivery_time_custom = utcDateTimeToTime(delivery_date_time_custom);

    delivery_information.delivery_time = delivery_time_custom;
    delivery_information.delivery_date_time = delivery_date_time_custom;

    return delivery_information;
  };

  const handleDeliveryType = (value) => {
    setEnableTimeEdit(true);
    const payment_type = module !== 'edit-order' ? (formik.values.delivery_information.delivery_type_id > 4 ? '' : formik.values.payment_type) : formik.values.payment_type;
    formik.setValues((v) => {
      if (v.delivery_information.delivery_type_id === 1 && value.id !== 1) {
        setMatrix([]);
        return {
          ...v,
          payment_type,
          delivery_information: {
            ...v.delivery_information,
            delivery_type_id: value.id,
            delivery_date: null,
            order_type: 0,
            delivery_time: "",
            charge: 0,
            upcharge_id: 0,
            upcharge_amount: 0,
            delivery_time_id: null,
            delivery_type: value.name,
            storeId:0,
            burq_quote_id:"",
            burq_quote_expires:""
          },
        };
      } else if (value.id === 1) {
        //For Pickup/Will-call, check assigned stores and set first index store id or null if no assigned stores
        const assignedStoreList = localStorage.getItem("assignStore")
        const assignedStoreListData = JSON.parse(assignedStoreList)
        const assignedStore = assignedStoreListData?.[0] ?? null
        const storeId = assignedStore?.id ?? null

        setMatrix([]);
        return {
          ...v,
          delivery_information: {
            ...v.delivery_information,
            delivery_type_id: value.id,
            delivery_date: null,
            order_type: 1,
            delivery_time: "07:00 AM",
            charge: 0,
            upcharge_id: 0,
            upcharge_amount: 0,
            delivery_time_id: null,
            delivery_type: value.name,
            storeId:storeId,
            burq_quote_id:"",
            burq_quote_expires:""
          },
          billing_info: {
            ...v.billing_info,
            zip_code_store_id: storeId,
            payment_method: assignedStore ? assignedStore.paymentgateway : ""
          },
          payabli_paypoint: assignedStore ? assignedStore.payabli_paypoint : "",
          payment_type
        };
      } else {
        if(module !== 'edit-order'){
           setMatrix([]);
           return {
            ...v,
            delivery_information: {
              ...v.delivery_information,
              delivery_type_id: value.id,
              order_type: 0,
              delivery_type: value.name,
              delivery_time_id: null,
              delivery_date: null,
              delivery_time: "",
              delivery_time_type: "",
              burq_quote_id:"",
              burq_quote_expires:""
  
            },
            payment_type
          };
        }else{
          return {
            ...v,
            delivery_information: {
              ...v.delivery_information,
              delivery_type_id: value.id,
              order_type: 0,
              delivery_type: value.name,
            },
            payment_type
          };
        }
        
      }
    });
  };

  const handleTimeChange = (value) => {
    if (!formik.values.delivery_information.delivery_date) {
      showToastMessage(
        undefined,
        `Please fill out delivery date before selecting time`,
        "error",
        false
      );
      return;
    }
    let delivery_information = { ...formik.values.delivery_information };

    let pst = moment(value).format("hh:mm A");
    let delivery_date_time = pstToUTCDateTime(
      delivery_information.delivery_date,
      null,
      pst,
      formik?.values?.orderTimezone
    );
    let delivery_time = utcDateTimeToTime(delivery_date_time);
    let upcharge_id = 0;
    let upcharge_amount = 0;
    let charge = 0;

    delivery_information.delivery_time = delivery_time;
    delivery_information.upcharge_id = upcharge_id;
    delivery_information.upcharge_amount = upcharge_amount;
    delivery_information.charge = charge;
    delivery_information.delivery_date_time = delivery_date_time;
    formik.setValues((v) => {
      return {
        ...v,
        delivery_information,
      };
    });
  };

  const handleDeliveryChargeChange = (value) => {
    // if (!formik.values.delivery_information.delivery_date && value !== 0 && value !== null && value !== '') {
    //   showToastMessage(
    //     undefined,
    //     `Please fill out delivery date before selecting time`,
    //     "error",
    //     false
    //   );
    //   return;
    // }
    let delivery_information = { ...formik.values.delivery_information };
    // debugger
    delivery_information.charge = parseFloat(value);
    formik.setValues((v) => {
      return {
        ...v,
        delivery_information,
      };
    });
  };

  //Update Upcharge value
  const handleUpchargeChange = (value) => {
    let delivery_information = { ...formik.values.delivery_information };
    delivery_information.upcharge_amount = parseFloat(value);
    formik.setValues((v) => {
      return {
        ...v,
        delivery_information,
      };
    });
  };

  const handleDeliveredBy = (value) => {

    let delivery_information = { ...formik.values.delivery_information };
    if (value === 'Anytime') {
      let pst = moment(moment().format('YYYY-MM-DD') + ' ' + CUSTOM_ANYTIME).format("hh:mm A");
      let delivery_date_time = pstToUTCDateTime(
        delivery_information.delivery_date,
        null,
        pst,
        formik?.values?.orderTimezone
      );
      let delivery_time = utcDateTimeToTime(delivery_date_time);
      delivery_information.delivery_time = delivery_time;
      delivery_information.delivery_date_time = delivery_date_time;
    }

    delivery_information.delivery_time_type = value;

    formik.setValues((v) => {
      return {
        ...v,
        delivery_information,
      };
    });
  };

  const handleDeliveryMatrix = ({
    charge,
    delivery_time_type,
    id,
    delivery_time,
    delivery_date_time,
    actual_delivered_time,
    name,
    burq_quote_id,
    burq_quote_expires
  }) => {
    let delivery_information = { ...formik.values.delivery_information };
    delivery_information.delivery_time_type = delivery_time_type;
    delivery_information.charge = charge;
    delivery_information.delivery_time_id = id;
    delivery_information.delivery_date_time = delivery_date_time;
    delivery_information.delivery_time = delivery_time;
    delivery_information.actual_delivered_time = actual_delivered_time;
    delivery_information.burq_quote_id = burq_quote_id?? ""
    delivery_information.burq_quote_expires = burq_quote_expires?? ""

    //IF CSR SELECT CUSTOM OPTION THNE WE ADD 3:00 PM BY DEFAULT
    if (id === -1) {
      let pstCustomTime = moment(moment().format('YYYY-MM-DD') + ' ' + CUSTOM_DEFAULT_TIME).format("hh:mm A");
      let delivery_date_time_custom = pstToUTCDateTime(
        delivery_information.delivery_date,
        null,
        pstCustomTime,
        formik?.values?.orderTimezone
      );
      let delivery_time_custom = utcDateTimeToTime(delivery_date_time_custom);

      delivery_information.delivery_time = delivery_time_custom;
      delivery_information.delivery_date_time = delivery_date_time_custom;
    }
    
    formik.setValues((v) => {
      return {
        ...v,
        delivery_information,
      };
    });

  };

  return {
    show,
    matrix,
    setMatrix,
    handleDeliveryMatrix,
    handleDateChange,
    handleDeliveryType,
    handleTimeChange,
    blockoutDates,
    tooltipDays,
    enableTimeEdit,
    setEnableTimeEdit,
    handleDeliveryChargeChange,
    handleDeliveredBy,
    handleUpchargeChange,
    timeslectionDropdown,
    setTimeslectionDropdown
  };
}
